import { useEffect, useMemo, type ComponentType } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Coins,
  FolderOpen,
  ListTodo,
  Package,
  Sparkles,
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { cn, formatCredits, formatDate, formatDateTime, getStatusColor } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type {
  OverviewAlert,
  OverviewMetrics,
  Product,
  Task,
  TaskType,
} from '@/types'

const TASK_LABELS: Record<TaskType, string> = {
  competitor_research: 'Competitor Research',
  variation: 'Variation',
  image_freestyle: 'Image Freestyle',
  video_freestyle: 'Video Freestyle',
  digital_human: 'Digital Human',
  idea_research: 'Idea Research',
  vertical_ideas: 'Vertical Ideas',
  foreplay_search: 'Ads Research',
  perplexity_search: 'Web Research',
  short_drama_mix: 'Drama Mix',
}

const PRODUCT_PAGE_SIZE = 100
const MAX_PRODUCT_PAGES = 10

interface MetricCardProps {
  title: string
  value: string
  subtitle: string
  to: string
  icon: ComponentType<{ className?: string }>
  iconStyle: string
  accentStyle: string
  unavailable?: boolean
}

async function fetchAllProducts(): Promise<Product[]> {
  let offset = 0
  let page = 0
  let total = Number.POSITIVE_INFINITY
  const items: Product[] = []

  while (offset < total && page < MAX_PRODUCT_PAGES) {
    const response = await api.getProducts(offset, PRODUCT_PAGE_SIZE)
    total = response.total
    if (response.items.length === 0) break
    items.push(...response.items)
    offset += response.items.length
    page += 1
  }

  return items
}

function parseTime(value?: string | null): number {
  if (!value) return 0
  const t = Date.parse(value)
  return Number.isNaN(t) ? 0 : t
}

function withinHours(value: string | null | undefined, hours: number): boolean {
  const timestamp = parseTime(value)
  if (!timestamp) return false
  return Date.now() - timestamp <= hours * 60 * 60 * 1000
}

function withinDays(value: string | null | undefined, days: number): boolean {
  const timestamp = parseTime(value)
  if (!timestamp) return false
  return Date.now() - timestamp <= days * 24 * 60 * 60 * 1000
}

function getPlanBadgeStyle(planType: string): string {
  switch (planType) {
    case 'trial':
      return 'text-gray-700 bg-gray-100'
    case 'basic':
      return 'text-blue-700 bg-blue-100'
    case 'pro':
      return 'text-purple-700 bg-purple-100'
    case 'enterprise':
      return 'text-amber-700 bg-amber-100'
    default:
      return 'text-gray-700 bg-gray-100'
  }
}

function MetricCard({
  title,
  value,
  subtitle,
  to,
  icon: Icon,
  iconStyle,
  accentStyle,
  unavailable = false,
}: MetricCardProps) {
  return (
    <Link
      to={to}
      className="group relative h-full min-h-[172px] overflow-hidden rounded-xl border border-gray-200/60 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className={cn('absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r', accentStyle)} />
      <div className={cn('inline-flex h-9 w-9 items-center justify-center rounded-lg', iconStyle)}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <p className={cn('mt-2 text-2xl font-bold text-gray-900', unavailable && 'text-red-600')}>{value}</p>
      <p
        className={cn(
          'mt-1 min-h-[40px] text-sm text-gray-500',
          unavailable && 'font-medium text-red-500'
        )}
      >
        {subtitle}
      </p>
      <span className="mt-auto inline-flex items-center gap-1 text-sm text-gray-500 transition-colors group-hover:text-gray-800">
        View
        <ArrowRight className="h-4 w-4" />
      </span>
    </Link>
  )
}

export function DashboardPage() {
  const { user, subscription, fetchUser, fetchSubscription } = useAuthStore()

  useEffect(() => {
    if (!user) fetchUser()
    if (!subscription) fetchSubscription()
  }, [user, subscription, fetchUser, fetchSubscription])

  const {
    data: productsData,
    isLoading: isProductsLoading,
    isError: isProductsError,
  } = useQuery({
    queryKey: ['products', 'overview'],
    queryFn: fetchAllProducts,
  })

  const {
    data: tasksData,
    isLoading: isTasksLoading,
    isError: isTasksError,
  } = useQuery({
    queryKey: ['tasks', 'overview'],
    queryFn: () => api.getTasks({ offset: 0, limit: 50 }),
  })

  const {
    data: assetStats,
    isLoading: isAssetStatsLoading,
    isError: isAssetStatsError,
  } = useQuery({
    queryKey: ['assets-stats', 'overview'],
    queryFn: () => api.getAssetStats(),
  })

  const products = useMemo<Product[]>(() => productsData ?? [], [productsData])
  const tasks = useMemo<Task[]>(() => tasksData?.items ?? [], [tasksData])

  const recentTasks = useMemo(() => {
    return [...tasks].sort((a, b) => parseTime(b.created_at) - parseTime(a.created_at)).slice(0, 5)
  }, [tasks])

  const recentProducts = useMemo(() => {
    return [...products].sort((a, b) => parseTime(b.updated_at) - parseTime(a.updated_at)).slice(0, 5)
  }, [products])

  const failedTasksInLast24h = useMemo(() => {
    return tasks.filter(
      (task) =>
        task.status === 'failed' && withinHours(task.completed_at ?? task.created_at, 24)
    )
  }, [tasks])

  const metrics = useMemo<OverviewMetrics>(() => {
    const activeTasksCount = tasks.filter(
      (task) => task.status === 'pending' || task.status === 'processing'
    ).length

    const output7d = tasks.reduce((sum, task) => {
      return withinDays(task.created_at, 7) ? sum + task.success_count : sum
    }, 0)

    const productsWithAssets = products.filter((product) => product.total_assets > 0).length

    return {
      active_tasks: activeTasksCount,
      output_7d: output7d,
      asset_library_total: assetStats?.total ?? 0,
      product_coverage_with_assets: productsWithAssets,
      product_coverage_total: products.length,
    }
  }, [tasks, products, assetStats])

  const alerts = useMemo<OverviewAlert[]>(() => {
    const nextAlerts: OverviewAlert[] = []

    if (subscription) {
      const lowCreditThreshold = Math.max(5, Math.ceil(subscription.credits_total * 0.1))
      if (subscription.credits_available <= lowCreditThreshold) {
        nextAlerts.push({
          id: 'low-credits',
          level: 'warning',
          title: 'Credits are running low',
          description: `Only ${formatCredits(subscription.credits_available)} credits left. Top up to avoid generation interruptions.`,
          action_label: 'Top Up Credits',
          action_to: '/settings/subscription',
        })
      }
    }

    if (failedTasksInLast24h.length > 0) {
      nextAlerts.push({
        id: 'failed-tasks-24h',
        level: 'danger',
        title: `${failedTasksInLast24h.length} task${failedTasksInLast24h.length > 1 ? 's' : ''} failed in the last 24h`,
        description: 'Review and retry failed jobs to restore delivery throughput.',
        action_label: 'View Failed Tasks',
        action_to: '/tasks',
      })
    }

    if (products.length === 0 && !isProductsError) {
      nextAlerts.push({
        id: 'no-products',
        level: 'info',
        title: 'No products configured yet',
        description: 'Create your first product to unlock research and generation workflows.',
        action_label: 'Create Product',
        action_to: '/products/new',
      })
    } else if (products.length > 0 && (assetStats?.total ?? 0) === 0 && !isAssetStatsError) {
      nextAlerts.push({
        id: 'no-assets',
        level: 'info',
        title: 'No assets in your library yet',
        description: 'Run your first generation task or upload assets to build a reusable library.',
        action_label: 'Start Generating',
        action_to: '/generate/image/competitor',
      })
    }

    return nextAlerts
  }, [
    subscription,
    failedTasksInLast24h,
    products.length,
    isProductsError,
    assetStats?.total,
    isAssetStatsError,
  ])

  const hasProducts = products.length > 0
  const hasAssets = (assetStats?.total ?? 0) > 0
  const hasServiceError = isProductsError || isAssetStatsError || isTasksError

  const displayPlanType = subscription?.is_expired ? 'trial' : subscription?.plan_type
  const displayPlanLabel = subscription?.is_expired ? 'trial' : subscription?.plan_type

  const subscriptionCreditsRemaining = subscription
    ? Math.max(
        0,
        subscription.subscription_credits ?? (subscription.credits_total - subscription.credits_used)
      )
    : 0
  const subscriptionCreditsTotal = subscription ? Math.max(0, subscription.credits_total) : 0
  const creditsProgress = subscriptionCreditsTotal
    ? Math.min(100, Math.max(0, (subscriptionCreditsRemaining / subscriptionCreditsTotal) * 100))
    : 0

  const heroMessage = useMemo(() => {
    if (hasServiceError) {
      return 'Some services are temporarily unavailable. Core actions remain accessible.'
    }
    if (metrics.active_tasks > 0) {
      return `${metrics.active_tasks} active task${metrics.active_tasks > 1 ? 's are' : ' is'} currently running.`
    }
    if (!hasProducts) {
      return 'Start by adding your first product to unlock guided generation workflows.'
    }
    if (!hasAssets) {
      return 'Your product is ready. Generate or upload assets to start campaign execution.'
    }
    return 'All systems are set. Keep shipping new creative variants today.'
  }, [hasAssets, hasProducts, hasServiceError, metrics.active_tasks])

  const metricCards: MetricCardProps[] = [
    {
      title: 'Active Tasks',
      value: isTasksError ? 'Unavailable' : String(metrics.active_tasks),
      subtitle: isTasksError
        ? 'Task service is currently unavailable.'
        : isTasksLoading
        ? 'Loading active workload.'
        : 'Pending and processing tasks.',
      to: '/tasks',
      icon: ListTodo,
      iconStyle: 'text-spectrum-blue bg-spectrum-blue/10',
      accentStyle: 'from-spectrum-blue via-spectrum-cyan to-spectrum-teal',
      unavailable: isTasksError,
    },
    {
      title: '7d Output',
      value: isTasksError ? 'Unavailable' : formatCredits(metrics.output_7d),
      subtitle: isTasksError
        ? 'Task service is currently unavailable.'
        : isTasksLoading
        ? 'Calculating weekly throughput.'
        : 'Successful outputs in the last 7 days.',
      to: '/tasks',
      icon: Sparkles,
      iconStyle: 'text-spectrum-teal bg-spectrum-teal/10',
      accentStyle: 'from-spectrum-teal via-spectrum-cyan to-spectrum-blue',
      unavailable: isTasksError,
    },
    {
      title: 'Asset Library',
      value: isAssetStatsError ? 'Unavailable' : formatCredits(metrics.asset_library_total),
      subtitle: isAssetStatsError
        ? 'Asset stats service unavailable.'
        : isAssetStatsLoading
        ? 'Loading library totals.'
        : `${formatCredits(assetStats?.image ?? 0)} images / ${formatCredits(assetStats?.video ?? 0)} videos`,
      to: '/library',
      icon: FolderOpen,
      iconStyle: 'text-spectrum-cyan bg-spectrum-cyan/10',
      accentStyle: 'from-spectrum-cyan via-spectrum-blue to-spectrum-purple',
      unavailable: isAssetStatsError,
    },
    {
      title: 'Product Coverage',
      value: isProductsError
        ? 'Unavailable'
        : `${metrics.product_coverage_with_assets}/${metrics.product_coverage_total}`,
      subtitle: isProductsError
        ? 'Product service is currently unavailable.'
        : isProductsLoading
        ? 'Loading product coverage.'
        : 'Products with at least one asset.',
      to: '/products',
      icon: Package,
      iconStyle: 'text-spectrum-magenta bg-spectrum-magenta/10',
      accentStyle: 'from-spectrum-magenta via-spectrum-purple to-spectrum-blue',
      unavailable: isProductsError,
    },
  ]

  return (
    <div className="space-y-6">
      <section className="overview-hero-bg relative overflow-hidden rounded-2xl border border-gray-200/70 p-6 shadow-sm lg:p-7">
        <div className="relative grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_1fr_auto]">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.username || user?.email?.split('@')[0] || 'User'}.
            </h1>
            <p className="text-gray-600">Create faster, launch smarter, and grow with every campaign.</p>
            <p className="text-sm font-medium text-gray-500">{heroMessage}</p>
          </div>

          <div className="space-y-3 rounded-xl border border-gray-200/80 bg-white/90 p-4 backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subscription Snapshot</p>
            {subscription ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
                      getPlanBadgeStyle(displayPlanType || 'trial')
                    )}
                  >
                    {displayPlanLabel}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                    <Coins className="h-4 w-4 text-amber-500" />
                    {formatCredits(subscription.credits_available)} credits
                  </span>
                </div>

                <div className="h-2 w-[15.6rem] max-w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-spectrum-teal to-spectrum-blue transition-all duration-500"
                    style={{ width: `${creditsProgress}%` }}
                  />
                </div>

                <p className="text-xs text-gray-500">
                  {subscription.is_expired
                    ? `Expired on ${formatDate(subscription.current_period_end)}`
                    : `Period ends on ${formatDate(subscription.current_period_end)}`}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Subscription status is loading.</p>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row xl:flex-col">
            <Link to="/generate/image/competitor" className="w-full">
              <Button className="w-full bg-gray-900 text-white hover:bg-gray-800">Start Creating</Button>
            </Link>
            <Link to="/tasks" className="w-full">
              <Button variant="outline" className="w-full">
                View Tasks
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200/60 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200/60 p-4">
            <h2 className="text-sm font-semibold text-gray-900">Attention</h2>
            {alerts.length === 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" />
                All clear
              </span>
            )}
          </div>
          <div className="divide-y">
            {alerts.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">No urgent alerts. Keep shipping.</div>
            ) : (
              alerts.map((alert) => (
                <div key={alert.id} className="p-4">
                  <div
                    className={cn(
                      'rounded-lg border p-3',
                      alert.level === 'danger' && 'border-red-200 bg-red-50/70',
                      alert.level === 'warning' && 'border-amber-200 bg-amber-50/70',
                      alert.level === 'info' && 'border-blue-200 bg-blue-50/70'
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle
                        className={cn(
                          'mt-0.5 h-4 w-4',
                          alert.level === 'danger' && 'text-red-500',
                          alert.level === 'warning' && 'text-amber-500',
                          alert.level === 'info' && 'text-blue-500'
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">{alert.title}</p>
                        <p className="mt-1 text-xs text-gray-600">{alert.description}</p>
                        <Link to={alert.action_to} className="mt-3 inline-flex">
                          <Button size="sm" variant="outline">
                            {alert.action_label}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200/60 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200/60 p-4">
            <h2 className="text-sm font-semibold text-gray-900">Recent Tasks</h2>
            <Link to="/tasks" className="text-sm text-primary hover:underline">
              View all
            </Link>
          </div>
          <div className="divide-y">
            {isTasksError && <div className="p-4 text-sm text-red-500">Unable to load tasks right now.</div>}
            {!isTasksError && isTasksLoading && (
              <div className="p-4 text-sm text-gray-500">Loading recent tasks...</div>
            )}
            {!isTasksError && !isTasksLoading && recentTasks.length === 0 && (
              <div className="p-4 text-sm text-gray-500">No tasks yet. Start a generation flow.</div>
            )}
            {!isTasksError &&
              !isTasksLoading &&
              recentTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between gap-3 p-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {TASK_LABELS[task.task_type] ?? task.task_type}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {task.success_count}/{task.requested_count} completed - {formatDateTime(task.created_at)}
                    </p>
                  </div>
                  <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium', getStatusColor(task.status))}>
                    {task.status}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200/60 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200/60 p-4">
          <h2 className="text-sm font-semibold text-gray-900">Recent Products</h2>
          <Link to="/products" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </div>

        {isProductsError && <div className="p-4 text-sm text-red-500">Unable to load products right now.</div>}
        {!isProductsError && isProductsLoading && (
          <div className="p-4 text-sm text-gray-500">Loading recent products...</div>
        )}
        {!isProductsError && !isProductsLoading && recentProducts.length === 0 && (
          <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
            <FolderOpen className="h-4 w-4 text-gray-400" />
            No products yet. Create one to start.
          </div>
        )}

        {!isProductsError && !isProductsLoading && recentProducts.length > 0 && (
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
            {recentProducts.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="group rounded-xl border border-gray-200/70 bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md"
              >
                <p className="truncate text-sm font-semibold text-gray-900">{product.product_name}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {product.total_assets} assets - Updated {formatDate(product.updated_at)}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-gray-500 transition-colors group-hover:text-gray-800">
                  Open
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
